/**
 * Footer - Application footer component
 *
 * Features:
 * - Copyright notice with brand name
 * - Navigation links: Terms, Privacy, About, Contact, Feedback
 * - Responsive: horizontal on desktop, stacked on mobile
 * - Institutional design with gold accent
 * - Accessible: proper ARIA labels, keyboard navigation
 * - Mobile: simplified design with touch-friendly targets
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { BRAND_NAME, APP_VERSION } from '../../config/branding';
import { useAppDispatch } from '../../store';
import {
  openTermsModal,
  openPrivacyModal,
  openDisclaimerModal,
  openAboutModal,
  openContactSupportModal,
  openFeedbackModal,
} from '../../store/slices/modalsSlice';

interface FooterLink {
  label: string;
  ariaLabel: string;
  onClick: () => void;
}

interface FooterProps {
  /**
   * When true, collapses the footer to zero height without unmounting.
   * Preserves Redux subscriptions and modal-trigger handlers (they just
   * can't fire while hidden). Used by Deep Analysis → Live Chart sub-tab
   * to give the chart ~50px more vertical space.
   */
  hidden?: boolean;
}

const Footer: React.FC<FooterProps> = React.memo(({ hidden = false }) => {
  const dispatch = useAppDispatch();
  const currentYear = new Date().getFullYear();

  const footerLinks: FooterLink[] = [
    { label: 'Terms', ariaLabel: 'Terms and Conditions', onClick: () => dispatch(openTermsModal()) },
    { label: 'Privacy', ariaLabel: 'Privacy Policy', onClick: () => dispatch(openPrivacyModal()) },
    { label: 'Disclaimer', ariaLabel: 'Legal Disclaimer', onClick: () => dispatch(openDisclaimerModal()) },
    { label: 'About', ariaLabel: 'About Stock B AI', onClick: () => dispatch(openAboutModal()) },
    { label: 'Contact', ariaLabel: 'Contact Support', onClick: () => dispatch(openContactSupportModal()) },
    { label: 'Feedback', ariaLabel: 'Send Feedback', onClick: () => dispatch(openFeedbackModal()) },
  ];

  return (
    <footer
      className={cn(
        'w-full',
        // Hidden on mobile, visible on desktop
        'hidden md:flex',
        'flex-row justify-start items-center gap-8',
        'py-1.5 px-6',
        'bg-surface/90 backdrop-blur-sm',
        'border-t border-border-subtle',
        // Class-based collapse (no unmount): preserves subscriptions/handlers
        // and avoids layout flicker when toggling sub-tabs. Belt-and-suspenders
        // !important on border-0 so it overrides the base `border-t` class
        // regardless of cn() insertion order.
        hidden && 'md:!h-0 overflow-hidden !border-0 !py-0 opacity-0 pointer-events-none'
      )}
      role="contentinfo"
      aria-label="Application footer"
    >
      {/* Copyright */}
      <div
        className={cn(
          'flex items-center gap-1',
          'text-xs md:text-sm text-text-muted',
          'mb-3 md:mb-0',
          'text-center'
        )}
      >
        <span aria-hidden="true">&copy;</span>
        <span>
          {currentYear} {BRAND_NAME}
          <sup aria-hidden="true" className="ml-0.5">®</sup>
        </span>
        <span className="hidden sm:inline text-text-disabled mx-1 md:mx-2" aria-hidden="true">|</span>
        <span className="text-xs text-text-disabled">
          v{APP_VERSION}
        </span>
      </div>

      {/* Navigation Links - Desktop: horizontal, Mobile: horizontal scrollable with larger touch targets */}
      <nav
        className={cn(
          'flex flex-row flex-wrap justify-center items-center',
          'gap-x-2 md:gap-x-4',
          'text-xs md:text-sm',
          'max-w-full'
        )}
        aria-label="Footer navigation"
      >
        {footerLinks.map((link, index) => (
          <React.Fragment key={link.label}>
            <button
              type="button"
              onClick={link.onClick}
              className={cn(
                'relative',
                // Base styles - muted text
                'text-text-muted',
                // Hover - gold accent with transition
                'hover:text-accent-primary',
                'focus:text-accent-primary',
                // Focus ring for accessibility
                'focus:outline-none',
                'focus-visible:ring-2',
                'focus-visible:ring-accent-primary',
                'focus-visible:ring-offset-2',
                'focus-visible:ring-offset-surface',
                // Transition
                'transition-colors duration-200',
                // Larger padding for better touch target on mobile
                'py-2 px-2.5 md:py-1 md:px-1.5',
                'min-h-[36px] md:min-h-auto',
                // Underline on hover (hidden on mobile to save space)
                'md:after:absolute md:after:bottom-0 md:after:left-1/2',
                'md:after:-translate-x-1/2 md:after:w-0 md:after:h-px',
                'md:after:bg-accent-primary',
                'hover:md:after:w-full',
                'md:after:transition-all md:after:duration-200',
                // Remove default button styles
                'bg-transparent border-none cursor-pointer',
                'whitespace-nowrap'
              )}
              aria-label={link.ariaLabel}
            >
              {link.label}
            </button>
            {/* Separator - hidden on mobile, visible on desktop between links */}
            {index < footerLinks.length - 1 && (
              <span
                className="hidden md:inline text-text-disabled text-xs"
                aria-hidden="true"
              >
                •
              </span>
            )}
          </React.Fragment>
        ))}
      </nav>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;